import React, { useState, useEffect, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetFeedbackAnalyticsSummary,
  useGetTopics,
} from "../../../api/queries";
import { useResolveFeedback } from "../../../api/mutations";
import { usePermissions } from "../../../contexts/PermissionContext";
import Combobox, { ComboboxOptionI } from "../../../componentLibrary/Combobox";
import Card from "../../../componentLibrary/Card";
import Spinner from "../../../componentLibrary/Spinner";
import Button from "../../../componentLibrary/Button";
import FeedbackCard from "../../../components/FeedbackCard";
import {
  UserSegmentEnum,
  GeneralFeedbackTypeEnum,
  FeedbackI,
  LessonI,
} from "../../../types";
import { getPageTitleFromUrl } from "../../../utils/pageTitleFromUrl";

interface FeedbackDisplayWidgetProps {
  selectedSegments: UserSegmentEnum[];
  startDate?: string;
  endDate?: string;
  externalLessonId?: string;
  externalTopicId?: string;
  onClearExternalSelection?: () => void;
}

interface FeedbackWithContext extends FeedbackI {
  moduleTitle?: string;
  lessonTitle?: string;
  pageTitle?: string;
  pageUrl?: string;
  moduleId?: string;
}

/**
 * Converts a string to Title Case by splitting on a delimiter and capitalizing each word.
 * @param str - The string to convert
 * @param delimiter - The delimiter to split on (default: "_")
 * @returns The title-cased string
 */
function toTitleCase(str: string, delimiter: string = "_"): string {
  return str
    .split(delimiter)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

/**
 * Generates feedback type options dynamically from the enum values.
 * @returns Array of combobox options with "All Types" plus generated options
 */
function getFeedbackTypeOptions(): ComboboxOptionI[] {
  const options: ComboboxOptionI[] = [{ value: "", label: "All Types" }];

  // Generate options from enum values
  Object.values(GeneralFeedbackTypeEnum).forEach((enumValue) => {
    options.push({
      value: enumValue,
      label: toTitleCase(enumValue, "_"),
    });
  });

  return options;
}

const feedbackTypeOptions: ComboboxOptionI[] = getFeedbackTypeOptions();

type ResolutionFilter = "all" | "unresolved" | "resolved";

const FeedbackDisplayWidget = ({
  selectedSegments,
  startDate,
  endDate,
  externalLessonId,
  externalTopicId,
  onClearExternalSelection,
}: FeedbackDisplayWidgetProps) => {
  const [selectedFeedbackType, setSelectedFeedbackType] = useState<string>("");
  const [topicId, setTopicId] = useState<string>("");
  const [lessonId, setLessonId] = useState<string>("");
  const [lessonOptions, setLessonOptions] = useState<LessonI[]>([]);
  const [isExternalSelection, setIsExternalSelection] =
    useState<boolean>(false);
  const [page, setPage] = useState(1);
  /** Default: unresolved only (same as hiding resolved by default). */
  const [resolutionFilter, setResolutionFilter] =
    useState<ResolutionFilter>("unresolved");
  const [resolvingId, setResolvingId] = useState<string | null>(null);

  const queryClient = useQueryClient();
  const { hasAllPermissions } = usePermissions();
  const canResolve = hasAllPermissions(["content:update", "analytics:update"]);

  const resolveMutation = useResolveFeedback({
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["feedbackAnalyticsSummary"],
      });
      await queryClient.invalidateQueries({ queryKey: ["feedback"] });
      setResolvingId(null);
    },
    onError: () => {
      setResolvingId(null);
    },
  });

  const handleResolveToggle = (feedbackId: string, isResolved: boolean) => {
    setResolvingId(feedbackId);
    resolveMutation.mutate({ feedbackId, isResolved });
  };

  const { data: topics, isLoading: isLoadingTopics } = useGetTopics();

  // Sync external selection with internal state
  useEffect(() => {
    if (externalLessonId && externalTopicId) {
      setIsExternalSelection(true);
      setTopicId(externalTopicId);
      setLessonId(externalLessonId);
    } else if (externalLessonId === "" && externalTopicId === "") {
      // Clear selection when external selection is cleared
      setIsExternalSelection(false);
      setTopicId("");
      setLessonId("");
    }
  }, [externalLessonId, externalTopicId]);

  const {
    data: feedbackData,
    isLoading,
    isError,
  } = useGetFeedbackAnalyticsSummary(
    selectedSegments,
    startDate,
    endDate,
    selectedFeedbackType || undefined,
    page,
    undefined, // limit uses default
    topicId || undefined,
    lessonId || undefined,
  );

  // Reset to page 1 when filters that affect the API change
  useEffect(() => {
    setPage(1);
  }, [
    selectedSegments,
    startDate,
    endDate,
    selectedFeedbackType,
    topicId,
    lessonId,
  ]);

  // Set available lessons when topic changes
  useEffect(() => {
    if (topics && topicId) {
      const selectedTopic = topics.find((topic) => topic._id === topicId);
      if (selectedTopic) {
        setLessonOptions(selectedTopic.lessons);
      }
    } else {
      setLessonOptions([]);
    }
  }, [topics, topicId]);

  // Reset lessonId when topicId changes manually (but not when it's from external selection)
  useEffect(() => {
    if (!isExternalSelection && topicId !== "") {
      setLessonId("");
    }
  }, [topicId]);

  // Reset the external selection flag after the lesson options are set
  useEffect(() => {
    if (isExternalSelection && lessonOptions.length > 0) {
      setIsExternalSelection(false);
    }
  }, [lessonOptions, isExternalSelection]);

  // API returns feedback already filtered by topic/lesson when those filters are set
  const recentFeedback = feedbackData?.recentFeedback ?? [];

  const unresolvedOnPage = useMemo(
    () => recentFeedback.filter((f) => f.isResolved !== true).length,
    [recentFeedback],
  );

  const resolvedOnPage = useMemo(
    () => recentFeedback.filter((f) => f.isResolved === true).length,
    [recentFeedback],
  );

  const displayedFeedback = useMemo(() => {
    if (resolutionFilter === "all") return recentFeedback;
    if (resolutionFilter === "resolved") {
      return recentFeedback.filter((item) => item.isResolved === true);
    }
    return recentFeedback.filter((item) => item.isResolved !== true);
  }, [recentFeedback, resolutionFilter]);

  const showingText = useMemo(() => {
    const totalCount = feedbackData?.totalCount ?? 0;
    const totalPages = feedbackData?.totalPages ?? 1;
    const currentPageNum = feedbackData?.page ?? page;
    const loadedOnPage = recentFeedback.length;
    const visible = displayedFeedback.length;

    const items = (n: number) => (n === 1 ? "item" : "items");

    if (totalCount === 0) {
      return "Nothing matches your filters.";
    }

    const pageLabel =
      totalPages > 1 ? `Page ${currentPageNum} of ${totalPages} · ` : "";

    if (loadedOnPage === 0) {
      return `${pageLabel}Nothing on this page · ${totalCount} ${items(totalCount)} in total`;
    }

    return `${pageLabel}${visible} of ${loadedOnPage} ${items(loadedOnPage)} on this page · ${totalCount} ${items(totalCount)} in total`;
  }, [
    feedbackData?.totalCount,
    feedbackData?.totalPages,
    feedbackData?.page,
    page,
    recentFeedback.length,
    displayedFeedback.length,
  ]);

  const allOnPage = recentFeedback.length;

  const getFeedbackContext = (feedback: FeedbackWithContext): string | null => {
    const isLessonFeedback =
      feedback.feedbackType === GeneralFeedbackTypeEnum.LESSON_FEEDBACK;
    const parts: string[] = [];

    // For lesson feedback, always prioritize showing topic and lesson names (API: moduleTitle)
    if (isLessonFeedback) {
      if (feedback.moduleTitle) {
        parts.push(feedback.moduleTitle);
      }
      if (feedback.lessonTitle) {
        parts.push(feedback.lessonTitle);
      }
    } else {
      // For other feedback types, show topic and lesson if available (API: moduleTitle)
      if (feedback.moduleTitle) {
        parts.push(feedback.moduleTitle);
      }
      if (feedback.lessonTitle) {
        parts.push(feedback.lessonTitle);
      }

      // Show page title if available from database lookup
      // If not available and it's not a lesson page, try to parse from URL
      if (feedback.pageTitle) {
        parts.push(feedback.pageTitle);
      } else if (
        feedback.pageNumber !== undefined &&
        feedback.pageNumber !== null
      ) {
        parts.push(`Page ${feedback.pageNumber}`);
      } else if (!feedback.lessonId && feedback.pageUrl) {
        // Only use URL parsing as fallback if it's not a lesson page
        const titleFromUrl = getPageTitleFromUrl(feedback.pageUrl);
        if (titleFromUrl) {
          parts.push(titleFromUrl);
        }
      }
    }

    return parts.length > 0 ? parts.join(" • ") : null;
  };

  const handleFeedbackCardClick = (feedback: FeedbackWithContext) => {
    // Extract IDs from feedback
    let feedbackLessonId: string | undefined;
    let feedbackTopicId: string | undefined;

    // Convert lessonId to string
    if (feedback.lessonId) {
      if (typeof feedback.lessonId === "string") {
        feedbackLessonId = feedback.lessonId;
      } else {
        const obj = feedback.lessonId as {
          _id?: { toString: () => string };
          toString?: () => string;
        };
        feedbackLessonId = obj._id?.toString() || obj.toString?.();
      }
    }

    // Convert moduleId (API) to string — topic id for navigation
    if (feedback.moduleId) {
      if (typeof feedback.moduleId === "string") {
        feedbackTopicId = feedback.moduleId;
      } else {
        const obj = feedback.moduleId as {
          _id?: { toString: () => string };
          toString?: () => string;
        };
        feedbackTopicId = obj._id?.toString() || obj.toString?.();
      }
    }

    // If topic id is missing but we have a lessonId, find the topic that contains this lesson
    if (!feedbackTopicId && feedbackLessonId && topics) {
      const topicContainingLesson = topics.find((topic) =>
        topic.lessons.some((lesson) => lesson._id === feedbackLessonId),
      );
      if (topicContainingLesson) {
        feedbackTopicId = topicContainingLesson._id;
      }
    }

    // If we have both lesson and topic, open the specific lesson page in a new tab
    if (feedbackLessonId && feedbackTopicId) {
      // If there's a specific page number, navigate to it
      // Note: pageNumber is stored as 1-based (display number), but URL expects 0-based index
      const pageParam =
        feedback.pageNumber !== undefined && feedback.pageNumber !== null
          ? `?page=${feedback.pageNumber - 1}`
          : "";
      window.open(
        `/topics/${feedbackTopicId}/sections/${feedbackLessonId}${pageParam}`,
        "_blank",
      );
    }
    // If we only have topic, open the topic page in a new tab
    else if (feedbackTopicId) {
      window.open(`/topics/${feedbackTopicId}`, "_blank");
    }
    // If we have a pageUrl (for page feedback), open that URL in a new tab
    else if (feedback.pageUrl) {
      window.open(feedback.pageUrl, "_blank");
    }
  };

  function getTopicComboboxValue() {
    if (!topics || !topicId) return { value: "", label: "All Topics" };
    const topicObj = topics.find((topic) => topic._id === topicId);
    return topicObj
      ? { value: topicObj._id, label: topicObj.title }
      : { value: "", label: "All Topics" };
  }

  function getLessonComboboxValue() {
    if (!topics || !topicId || !lessonId)
      return { value: "", label: "All Lessons" };
    const topicObj = topics.find((topic) => topic._id === topicId);
    if (topicObj) {
      const lessonObj = topicObj.lessons.find(
        (lesson) => lesson._id === lessonId,
      );
      return lessonObj
        ? { value: lessonObj._id, label: lessonObj.title }
        : { value: "", label: "All Lessons" };
    }
    return { value: "", label: "All Lessons" };
  }

  const topicOptions: ComboboxOptionI[] = [
    { value: "", label: "All Topics" },
    ...(topics?.map((topic) => ({
      value: topic._id,
      label: topic.title,
    })) || []),
  ];

  const lessonComboboxOptions: ComboboxOptionI[] = [
    { value: "", label: "All Lessons" },
    ...lessonOptions.map((lesson) => ({
      value: lesson._id || "",
      label: lesson.title,
    })),
  ];

  // Check if any filters are active (not at default values)
  const hasActiveFilters =
    topicId !== "" || lessonId !== "" || selectedFeedbackType !== "";

  if (isLoadingTopics) {
    return (
      <Card className="h-full space-y-4">
        <Spinner className="mx-auto" />
      </Card>
    );
  }

  return (
    <Card className="h-full space-y-4">
      <h3 className="text-center m-0 mb-3 text-gray-700 text-lg underline">
        User Feedback
      </h3>

      {externalLessonId && externalTopicId && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <span className="text-sm text-blue-700">
            Filtered from chart selection
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Topic
          </label>
          <Combobox
            options={topicOptions}
            onChange={(value) => {
              setTopicId(value.value);
              setIsExternalSelection(false);
              onClearExternalSelection?.();
            }}
            value={getTopicComboboxValue()}
            placeholder="All Topics"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Lesson
          </label>
          <Combobox
            options={lessonComboboxOptions}
            onChange={(value) => {
              setLessonId(value.value);
              setIsExternalSelection(false);
              onClearExternalSelection?.();
            }}
            value={getLessonComboboxValue()}
            placeholder="All Lessons"
            disabled={!topicId || lessonOptions.length === 0}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Feedback Type
          </label>
          <Combobox
            options={feedbackTypeOptions}
            onChange={(value) => setSelectedFeedbackType(value.value)}
            value={
              feedbackTypeOptions.find(
                (opt) => opt.value === selectedFeedbackType,
              ) || feedbackTypeOptions[0]
            }
            placeholder="All Types"
          />
        </div>
        <div className="flex items-end">
          <Button
            variant="secondary"
            size="md"
            onClick={() => {
              setTopicId("");
              setLessonId("");
              setSelectedFeedbackType("");
              setIsExternalSelection(false);
              onClearExternalSelection?.();
            }}
            className={`w-full ${
              hasActiveFilters
                ? "!bg-gray-hover !text-white !border-gray-hover hover:!bg-gray-600"
                : ""
            }`}
            disabled={!hasActiveFilters}
          >
            Clear Filters
          </Button>
        </div>
      </div>

      <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <span className="text-sm text-gray-600">{showingText}</span>
          <div
            className="inline-flex rounded-lg border border-gray-300 bg-white overflow-hidden shadow-sm"
            role="radiogroup"
            aria-label="Filter by resolution status"
          >
            {(
              [
                "all",
                "unresolved",
                "resolved",
              ] as const satisfies readonly ResolutionFilter[]
            ).map((filterKey) => {
              const count =
                filterKey === "all"
                  ? allOnPage
                  : filterKey === "unresolved"
                    ? unresolvedOnPage
                    : resolvedOnPage;
              const selected = resolutionFilter === filterKey;
              return (
                <button
                  key={filterKey}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => setResolutionFilter(filterKey)}
                  className={`px-3 py-1.5 text-sm font-medium capitalize transition-colors border-r border-gray-300 last:border-r-0 ${
                    selected
                      ? "bg-electrum-300 text-white"
                      : "text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {filterKey} ({count})
                </button>
              );
            })}
          </div>
        </div>
        {(feedbackData?.totalPages ?? 1) > 1 && (
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              Previous
            </Button>
            <span className="text-sm text-gray-600">
              Page {feedbackData?.page ?? page} of{" "}
              {feedbackData?.totalPages ?? 1}
            </span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                setPage((p) => Math.min(feedbackData?.totalPages ?? 1, p + 1))
              }
              disabled={
                (feedbackData?.totalPages ?? 1) <= 0 ||
                page >= (feedbackData?.totalPages ?? 1)
              }
            >
              Next
            </Button>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <Spinner className="mx-auto" />
        </div>
      ) : isError ? (
        <div className="text-center p-4 text-red-600">
          Error loading feedback data
        </div>
      ) : recentFeedback.length === 0 ? (
        <div className="text-center p-4 text-gray-500">
          No feedback found for the selected filters
        </div>
      ) : displayedFeedback.length === 0 ? (
        <div className="text-center p-4 text-gray-500">
          Nothing on this page matches your status filters.
        </div>
      ) : (
        <div className="overflow-y-auto" style={{ maxHeight: "500px" }}>
          <div className="space-y-3">
            {displayedFeedback.map((feedback) => {
              const feedbackWithContext = feedback as FeedbackWithContext;
              const contextStr = getFeedbackContext(feedbackWithContext);

              return (
                <FeedbackCard
                  key={feedbackWithContext._id}
                  feedback={feedbackWithContext}
                  context={contextStr ?? undefined}
                  onContextClick={
                    contextStr
                      ? () => handleFeedbackCardClick(feedbackWithContext)
                      : undefined
                  }
                  showEmptyState
                  canResolve={canResolve}
                  onResolveToggle={handleResolveToggle}
                  isResolving={resolvingId === feedbackWithContext._id}
                />
              );
            })}
          </div>
        </div>
      )}
    </Card>
  );
};

export default FeedbackDisplayWidget;
